import jsPDF from "jspdf";

/**
 * Export du dossier préparatoire pour le notaire — STRICTEMENT value-free.
 *
 * Ne reçoit que les données de la RPC `notaire_dossier_oeuvres` (aucune valeur
 * financière, aucun fiscal) : titre, artiste, présence des pièces, manques.
 * Aligné §22.7 de la stratégie notaires (« Exports PDF / Excel »).
 */

const PIECES = [
  ["has_photo", "Photo"],
  ["has_facture", "Facture"],
  ["has_certificat", "Certificat"],
  ["has_provenance", "Provenance"],
];

const presentesOf = (o) => PIECES.filter(([k]) => o[k]).map(([, l]) => l);
const manquantesOf = (o) => {
  const m = PIECES.filter(([k]) => !o[k]).map(([, l]) => l);
  return o.a_verifier ? [...m, "Propriété à vérifier"] : m;
};
const safeName = (s) => (s || "dossier").replace(/[^a-zA-Z0-9_-]/g, "_");
const stamp = () => new Date().toISOString().slice(0, 10);

// ---------------------------------------------------------------------------
// PDF
// ---------------------------------------------------------------------------
export function generateNotaireDossierPDF({ nom, oeuvres = [] }) {
  const doc = new jsPDF();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 16;
  const contentW = pageW - margin * 2;
  const bottom = pageH - 18;
  let y = margin;

  const total = oeuvres.length;
  const completes = oeuvres.filter((o) => (o.manques ?? manquantesOf(o).length) === 0).length;
  const aReunir = total - completes;

  const ensure = (needed) => {
    if (y + needed > bottom) {
      doc.addPage();
      y = margin;
    }
  };
  const heading = (text, size = 13) => {
    ensure(12);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(size);
    doc.setTextColor(20, 20, 20);
    doc.text(text, margin, y);
    y += size * 0.5 + 3;
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, y, pageW - margin, y);
    y += 5;
  };
  const line = (text, opts = {}) => {
    const { size = 10, color = [60, 60, 60], gap = 5.5, bold = false } = opts;
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text, contentW);
    ensure(lines.length * gap);
    doc.text(lines, margin, y);
    y += lines.length * gap;
  };

  // En-tête
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(15, 15, 15);
  doc.text("Dossier art — préparation", margin, y);
  y += 8;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(120, 120, 120);
  doc.text("Artkiv — Transmission professionnelle", margin, y);
  y += 8;
  doc.setTextColor(60, 60, 60);
  if (nom) {
    doc.text(`Dossier : ${nom}`, margin, y);
    y += 5.5;
  }
  doc.text(`Établi le : ${new Date().toLocaleDateString("fr-FR")}`, margin, y);
  y += 10;

  // 1. Synthèse
  heading("1. Synthèse du dossier");
  line(`Œuvres : ${total}`);
  line(`Dossier complet : ${completes}`);
  line(`À compléter : ${aReunir}`);
  y += 4;

  // 2. Inventaire (présence des pièces, sans valeur)
  heading("2. Inventaire et pièces au dossier");
  oeuvres.forEach((o, i) => {
    ensure(14);
    line(`${i + 1}. ${o.titre || "Sans titre"} — ${o.artiste || "Artiste inconnu"}`, {
      bold: true,
      gap: 5,
    });
    const presentes = presentesOf(o);
    const manquantes = manquantesOf(o);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(90, 90, 90);
    const detail =
      `Pièces présentes : ${presentes.length ? presentes.join(", ") : "aucune"}` +
      (manquantes.length ? `  ·  À réunir : ${manquantes.join(", ")}` : "  ·  Dossier complet");
    const dl = doc.splitTextToSize(detail, contentW);
    ensure(dl.length * 5 + 3);
    doc.text(dl, margin, y);
    y += dl.length * 5 + 3;
  });
  y += 4;

  // 3. Pièces à réunir (récap actionnable)
  const incomplets = oeuvres.filter((o) => manquantesOf(o).length > 0);
  if (incomplets.length > 0) {
    heading("3. Pièces à réunir");
    line(`${incomplets.length} œuvre(s) nécessitent un ou plusieurs documents :`, {
      size: 9,
      color: [90, 90, 90],
      gap: 6,
    });
    incomplets.forEach((o, i) => {
      line(
        `${i + 1}. ${o.titre || "Sans titre"} — ${o.artiste || "Artiste inconnu"} : ${manquantesOf(
          o
        ).join(", ")}`,
        { size: 9, color: [60, 60, 60], gap: 5 }
      );
    });
    y += 4;
  }

  // Mention
  heading("Mention", 11);
  line(
    "ARTKIV prépare et structure les informations. Aucune valeur n'est affichée : le dossier de préparation ne se substitue pas à l'expertise du commissaire-priseur ou de l'expert mandaté, ni à l'appréciation juridique du notaire.",
    { size: 8, color: [120, 120, 120], gap: 4 }
  );

  // Pieds de page
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, pageH - 13, pageW - margin, pageH - 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(150, 150, 150);
    doc.text("Document généré par Artkiv — confidentiel, sans valeur d'expertise.", margin, pageH - 8);
    doc.text(`Page ${i} / ${totalPages}`, pageW - margin, pageH - 8, { align: "right" });
  }

  doc.save(`dossier_art_${safeName(nom)}_${stamp()}.pdf`);
}

// ---------------------------------------------------------------------------
// Excel
// ---------------------------------------------------------------------------
export async function generateNotaireDossierXLSX({ nom, oeuvres = [] }) {
  const XLSX = await import("xlsx");
  const wb = XLSX.utils.book_new();

  const total = oeuvres.length;
  const completes = oeuvres.filter((o) => manquantesOf(o).length === 0).length;

  // Synthèse
  const summary = [
    ["Dossier art — préparation"],
    [],
    ["Dossier", nom || ""],
    ["Établi le", new Date().toLocaleDateString("fr-FR")],
    [],
    ["Œuvres", total],
    ["Dossier complet", completes],
    ["À compléter", total - completes],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), "Synthèse");

  // Inventaire
  const invHead = ["#", "Titre", "Artiste", "Photo", "Facture", "Certificat", "Provenance", "Propriété à vérifier", "À réunir"];
  const invRows = [
    invHead,
    ...oeuvres.map((o, i) => [
      i + 1,
      o.titre || "Sans titre",
      o.artiste || "Artiste inconnu",
      o.has_photo ? "Oui" : "Non",
      o.has_facture ? "Oui" : "Non",
      o.has_certificat ? "Oui" : "Non",
      o.has_provenance ? "Oui" : "Non",
      o.a_verifier ? `Oui${o.verification_note ? " — " + o.verification_note : ""}` : "Non",
      manquantesOf(o).join(", "),
    ]),
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(invRows), "Inventaire");

  // Pièces à réunir
  const incomplets = oeuvres.filter((o) => manquantesOf(o).length > 0);
  if (incomplets.length > 0) {
    const rows = [
      ["#", "Titre", "Artiste", "Pièces manquantes"],
      ...incomplets.map((o, i) => [
        i + 1,
        o.titre || "Sans titre",
        o.artiste || "Artiste inconnu",
        manquantesOf(o).join(", "),
      ]),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), "À réunir");
  }

  XLSX.writeFile(wb, `dossier_art_${safeName(nom)}_${stamp()}.xlsx`);
}
